import type { BrowserMetroFiles } from './fileMap';
import { buildPreviewHtml, extractInlineSourceMap } from './previewDocument';
import type { ContentChange, WorkerResponse } from './protocol';
import { RN_PACKAGE_SERVER_URL as DEFAULT_PACKAGE_SERVER_URL } from '@/utils/constants';

export type RunnerLogLevel = 'info' | 'log' | 'warn' | 'error';

export interface RunnerLogEntry {
  level: RunnerLogLevel;
  text: string;
}

export interface RunnerError {
  source: 'bundler' | 'runtime';
  message: string;
  stack?: string;
}

export interface BrowserMetroRunnerCallbacks {
  /** Fired with the preview HTML blob URL after each full (re)build. */
  onPreviewUrl?: (url: string | undefined) => void;

  /** Fired once the initial watch build is ready. */
  onReady?: () => void;

  /** Fired after every build that produced a bundle — i.e. the project compiles. */
  onBuildSuccess?: () => void;

  /** Fired when the initial build fails before a preview exists. */
  onInitialBuildError?: (error: RunnerError) => void;

  /** Fired whenever bundling starts/stops. */
  onBundling?: (bundling: boolean) => void;

  /** Build lifecycle + forwarded runtime console output, destined for the log terminal. */
  onLog?: (entry: RunnerLogEntry) => void;

  /** Bundler + runtime errors, destined for the previewError / error-fix loop. */
  onError?: (error: RunnerError) => void;
}

/**
 * Main-thread wrapper around the browser-metro Web Worker. Owns the worker, the
 * preview HTML blob URLs, and the message bridge with the sandboxed iframe:
 *
 *   - `start(files)` / `update(changes)` / `stop()` drive the worker's watch mode
 *   - full (re)builds produce a new preview blob URL (via `onPreviewUrl`)
 *   - incremental HMR updates are posted straight to the iframe (no reload)
 *   - the iframe's forwarded `console.*` and runtime errors are surfaced via
 *     `onLog` / `onError`
 *
 * Mirrors the update/HMR flow of reactnative.run's playground harness.
 */
export class BrowserMetroRunner {
  #worker: Worker | null = null;
  #iframe: HTMLIFrameElement | null = null;
  #callbacks: BrowserMetroRunnerCallbacks;
  #packageServerUrl: string;
  #started = false;
  #initialBuildComplete = false;

  #jsBlobUrl = '';
  #htmlBlobUrl = '';
  #apiBlobUrl = '';
  #prevJsBlobUrl = '';
  #prevHtmlBlobUrl = '';
  #prevApiBlobUrl = '';
  #revokeTimer: ReturnType<typeof setTimeout> | null = null;
  #lastBundle = '';
  #lastApiBundle: string | null = null;

  #messageHandler = (e: MessageEvent) => this.#onIframeMessage(e);

  constructor(callbacks: BrowserMetroRunnerCallbacks = {}, packageServerUrl: string = DEFAULT_PACKAGE_SERVER_URL) {
    this.#callbacks = callbacks;
    this.#packageServerUrl = packageServerUrl;
  }

  get started(): boolean {
    return this.#started;
  }

  get previewUrl(): string {
    return this.#htmlBlobUrl;
  }

  /** Register (or clear) the preview iframe so HMR updates can be posted to it. */
  attachIframe(iframe: HTMLIFrameElement | null): void {
    this.#iframe = iframe;
  }

  /** Boot the worker (if needed) and start a watch build for `files`. */
  start(files: BrowserMetroFiles): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.#worker) {
      this.#worker = new Worker(new URL('./bundler.worker.ts', import.meta.url), { type: 'module' });
      this.#worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.#onWorkerMessage(e.data);

      this.#worker.onerror = (e: ErrorEvent) => {
        const detail =
          e.message || (e.error && (e.error.stack || e.error.message)) || 'unknown (worker failed to load or crashed)';
        const loc = e.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : '';
        const error = { source: 'bundler' as const, message: detail + loc };

        if (!this.#initialBuildComplete) {
          this.#handleInitialBuildError(error);
        } else {
          this.#callbacks.onError?.(error);
          this.#log('error', 'Bundler worker error: ' + detail + loc);
        }
      };
      window.addEventListener('message', this.#messageHandler);
    }

    this.#started = true;
    this.#initialBuildComplete = false;
    this.#callbacks.onBundling?.(true);
    this.#log('info', 'Bundling…');
    this.#worker.postMessage({ type: 'watch-start', files, packageServerUrl: this.#packageServerUrl });
  }

  /** Push a batch of file changes into the running watch build. */
  update(changes: ContentChange[]): void {
    if (!this.#worker || !this.#started || changes.length === 0) {
      return;
    }

    this.#callbacks.onBundling?.(true);
    this.#worker.postMessage({ type: 'watch-update', changes });
  }

  /** Stop the watch build and tear down the worker + blob URLs. */
  stop(): void {
    if (this.#worker) {
      try {
        this.#worker.postMessage({ type: 'watch-stop' });
      } catch {
        // worker may already be gone
      }

      this.#worker.terminate();
      this.#worker = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.#messageHandler);
    }

    // Revoke all blob URLs immediately on stop (no need to defer).
    if (this.#revokeTimer) {
      clearTimeout(this.#revokeTimer);
      this.#revokeTimer = null;
    }
    if (this.#prevJsBlobUrl) URL.revokeObjectURL(this.#prevJsBlobUrl);
    if (this.#prevHtmlBlobUrl) URL.revokeObjectURL(this.#prevHtmlBlobUrl);
    if (this.#prevApiBlobUrl) URL.revokeObjectURL(this.#prevApiBlobUrl);
    this.#prevJsBlobUrl = '';
    this.#prevHtmlBlobUrl = '';
    this.#prevApiBlobUrl = '';

    if (this.#jsBlobUrl) URL.revokeObjectURL(this.#jsBlobUrl);
    if (this.#htmlBlobUrl) URL.revokeObjectURL(this.#htmlBlobUrl);
    if (this.#apiBlobUrl) URL.revokeObjectURL(this.#apiBlobUrl);
    this.#jsBlobUrl = '';
    this.#htmlBlobUrl = '';
    this.#apiBlobUrl = '';

    this.#started = false;
    this.#initialBuildComplete = false;
    this.#lastBundle = '';
    this.#lastApiBundle = null;
    this.#callbacks.onBundling?.(false);
    this.#callbacks.onPreviewUrl?.(undefined);
  }

  dispose(): void {
    this.stop();
    this.#iframe = null;
  }

  // --- Worker messages --------------------------------------------------------

  #onWorkerMessage(data: WorkerResponse): void {
    switch (data.type) {
      case 'watch-ready': {
        this.#initialBuildComplete = true;
        this.#lastBundle = data.code;
        this.#lastApiBundle = data.apiBundle;
        this.#callbacks.onBundling?.(false);
        this.#callbacks.onReady?.();
        this.#callbacks.onBuildSuccess?.();
        this.#log('info', 'Preview ready' + (data.apiBundle ? ' (+ API routes)' : ''));
        this.#rebuildPreview(data.code, data.apiBundle);
        break;
      }

      case 'watch-rebuild': {
        this.#lastBundle = data.code;

        if (data.apiBundle !== null) {
          this.#lastApiBundle = data.apiBundle;
        }

        this.#callbacks.onBundling?.(false);
        this.#callbacks.onBuildSuccess?.();
        this.#log('info', 'Rebuilt preview');
        this.#rebuildPreview(data.code, data.apiBundle);
        break;
      }

      case 'hmr-update': {
        this.#lastBundle = data.bundle;
        this.#callbacks.onBundling?.(false);
        this.#callbacks.onBuildSuccess?.();

        // If API routes changed, a hot patch can't reflect them — full reload.
        if (data.apiBundle) {
          this.#lastApiBundle = data.apiBundle;
          this.#log('info', 'API routes updated, reloading preview');
          this.#rebuildPreview(data.bundle, data.apiBundle);
          break;
        }

        const count = Object.keys(data.update.updatedModules).length;
        this.#log('info', `HMR: updated ${count} module(s)`);
        const posted = this.#postToIframe({
          type: 'hmr-update',
          updatedModules: data.update.updatedModules,
          removedModules: data.update.removedModules,
          reverseDepsMap: data.update.reverseDepsMap,
        });

        if (!posted) {
          this.#log('info', 'HMR post failed, falling back to full reload');
          this.#rebuildPreview(data.bundle, this.#lastApiBundle);
        }
        break;
      }

      case 'watch-idle': {
        // Batch touched nothing the bundle cares about — just clear the spinner.
        this.#callbacks.onBundling?.(false);
        break;
      }

      case 'log': {
        // The worker's own console (browser-metro diagnostics), not the app's.
        this.#log(data.level, data.text);
        break;
      }

      case 'watch-stopped': {
        break;
      }

      case 'watch-start-error': {
        this.#handleInitialBuildError({ source: 'bundler', message: data.message });
        break;
      }

      case 'error': {
        this.#callbacks.onBundling?.(false);
        this.#log('error', data.message);
        this.#callbacks.onError?.({ source: 'bundler', message: data.message });
        break;
      }

      default: {
        break;
      }
    }
  }

  #handleInitialBuildError(error: RunnerError): void {
    this.#callbacks.onBundling?.(false);
    this.#log('error', error.message);

    // Terminating is important: withTimeout rejects its wrapper but cannot
    // cancel browser-metro's underlying build. Leaving that worker alive would
    // let a timed-out build keep mutating its graph during the retry.
    this.#worker?.terminate();
    this.#worker = null;
    this.#started = false;
    this.#initialBuildComplete = false;

    if (this.#callbacks.onInitialBuildError) {
      this.#callbacks.onInitialBuildError(error);
    } else {
      this.#callbacks.onError?.(error);
    }
  }

  // --- Iframe messages --------------------------------------------------------

  #onIframeMessage(e: MessageEvent): void {
    if (this.#iframe && e.source !== this.#iframe.contentWindow) {
      return;
    }

    const data = e.data;

    if (!data || typeof data !== 'object') {
      return;
    }

    if (data.type === 'console') {
      const level: RunnerLogLevel = data.method === 'log' ? 'log' : data.method;
      this.#log(level, data.text);
      return;
    }

    if (data.type === 'runtime-error') {
      const loc = data.file && data.line ? ` at ${data.file}:${data.line}` : '';
      const frames: string = Array.isArray(data.stack)
        ? data.stack.map((f: any) => `    at ${f.fn} (${f.file}:${f.line}:${f.column})`).join('\n')
        : '';
      this.#log('error', data.message + loc);
      this.#callbacks.onError?.({
        source: 'runtime',
        message: data.message,
        stack: (loc ? loc.trim() + '\n' : '') + frames,
      });
      return;
    }

    if (data.type === 'hmr-full-reload') {
      this.#log('info', 'HMR boundary not found, reloading…');

      if (this.#lastBundle) {
        this.#rebuildPreview(this.#lastBundle, this.#lastApiBundle);
      }
    }
  }

  #postToIframe(message: unknown): boolean {
    try {
      const win = this.#iframe?.contentWindow;
      if (!win) return false;
      win.postMessage(message, '*');
      return true;
    } catch {
      return false;
    }
  }

  // --- Preview blob assembly --------------------------------------------------

  #rebuildPreview(bundleCode: string, apiBundle: string | null | undefined): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.#revokeBlobUrls();

    const jsBlob = new Blob([bundleCode], { type: 'application/javascript' });
    this.#jsBlobUrl = URL.createObjectURL(jsBlob);

    const effectiveApi = apiBundle !== undefined ? apiBundle : this.#lastApiBundle;
    let apiBlobUrl: string | undefined;

    if (effectiveApi) {
      const apiBlob = new Blob([effectiveApi], { type: 'application/javascript' });
      this.#apiBlobUrl = URL.createObjectURL(apiBlob);
      apiBlobUrl = this.#apiBlobUrl;
    }

    const sourceMap = extractInlineSourceMap(bundleCode);
    const html = buildPreviewHtml(this.#jsBlobUrl, sourceMap, apiBlobUrl);
    const htmlBlob = new Blob([html], { type: 'text/html' });
    this.#htmlBlobUrl = URL.createObjectURL(htmlBlob);

    this.#callbacks.onPreviewUrl?.(this.#htmlBlobUrl);
  }

  #revokeBlobUrls(): void {
    // Revoke the previous generation immediately (it's no longer referenced).
    if (this.#prevJsBlobUrl) {
      URL.revokeObjectURL(this.#prevJsBlobUrl);
    }
    if (this.#prevHtmlBlobUrl) {
      URL.revokeObjectURL(this.#prevHtmlBlobUrl);
    }
    if (this.#prevApiBlobUrl) {
      URL.revokeObjectURL(this.#prevApiBlobUrl);
    }

    if (this.#revokeTimer) {
      clearTimeout(this.#revokeTimer);
    }

    // Hold current URLs for a short window so the iframe can finish loading them
    // before they're revoked (prevents white-screen on rapid rebuilds).
    this.#prevJsBlobUrl = this.#jsBlobUrl;
    this.#prevHtmlBlobUrl = this.#htmlBlobUrl;
    this.#prevApiBlobUrl = this.#apiBlobUrl;

    this.#jsBlobUrl = '';
    this.#htmlBlobUrl = '';
    this.#apiBlobUrl = '';

    const prevJs = this.#prevJsBlobUrl;
    const prevHtml = this.#prevHtmlBlobUrl;
    const prevApi = this.#prevApiBlobUrl;

    this.#revokeTimer = setTimeout(() => {
      if (prevJs) URL.revokeObjectURL(prevJs);
      if (prevHtml) URL.revokeObjectURL(prevHtml);
      if (prevApi) URL.revokeObjectURL(prevApi);
      this.#prevJsBlobUrl = '';
      this.#prevHtmlBlobUrl = '';
      this.#prevApiBlobUrl = '';
      this.#revokeTimer = null;
    }, 2000);
  }

  #log(level: RunnerLogLevel, text: string): void {
    // Mirror to the browser console so lifecycle/errors are visible in DevTools
    // even when the read-only terminal tab is not open.
    if (level === 'error') {
      console.error('[rn-runner]', text);
    } else {
      console.info('[rn-runner]', text);
    }

    this.#callbacks.onLog?.({ level, text });
  }
}
